import React, { useState, useEffect } from 'react';
import { Search, X, Phone, Calendar, CheckCircle, Clock, XCircle, Download, Shield,  Award, IndianRupee , Check } from 'lucide-react';
import { getData } from '../../../../services/apiService';

const PaymentStatus = () => {
    const [mobileNumber, setMobileNumber] = useState('');
    const [selectedEvent, setSelectedEvent] = useState('');
    const [events, setEvents] = useState([]);
    const [paymentStatus, setPaymentStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [fetchingEvents, setFetchingEvents] = useState(false);
    const [error, setError] = useState('');
    const [showModal, setShowModal] = useState(false);

    useEffect(() => {
        const fetchEvents = async () => {
            setFetchingEvents(true);
            try {
                const response = await getData('/Event/Eventlist');

                if (Array.isArray(response)) {
                    setEvents(response);
                } else if (response?.isError === false && Array.isArray(response?.result)) {
                    setEvents(response.result);
                } else if (Array.isArray(response?.Result)) {
                    setEvents(response.Result);
                } else {
                    setEvents([]);
                    setError('No events available');
                }
            } catch (error) {
                console.error('Error fetching events:', error);
                setEvents([]);
                setError('Unable to load events');
            } finally {
                setFetchingEvents(false);
            }
        };

        fetchEvents();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!mobileNumber.trim()) {
            setError('Please enter mobile number');
            return;
        }

        if (!selectedEvent) {
            setError('Please select an event');
            return;
        }

        const mobileRegex = /^[0-9]{10}$/;
        if (!mobileRegex.test(mobileNumber)) {
            setError('Please enter a valid 10-digit mobile number');
            return;
        }

        setPaymentStatus(null);
        setError('');
        setLoading(true);

        try {
            const response = await getData(
                `/Event/RegistrationStatus?eventId=${selectedEvent}&phone=${mobileNumber}`
            );

            if (response.isError) {
                setError(response.message || 'Failed to fetch payment status');
            } else {
                setPaymentStatus(response);
                setShowModal(true); // Show modal on success
            }
        } catch (err) {
            setError('Network error. Please try again.');
            console.error('Error:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleClear = () => {
        setMobileNumber('');
        setSelectedEvent('');
        setPaymentStatus(null);
        setError('');
        setShowModal(false);
    };

    const getSelectedEventName = () => {
        const event = events.find(e => e.Event_Id == selectedEvent);
        return event ? event.Event_Name : 'N/A';
    };

    const getStatusIcon = (status) => {
        switch (status?.toLowerCase()) {
            case 'success':
                return <CheckCircle className="w-10 h-10 text-green-500" />;
            case 'pending':
                return <Clock className="w-10 h-10 text-amber-500" />;
            case 'failed':
                return <XCircle className="w-10 h-10 text-red-500" />;
            default:
                return <Shield className="w-10 h-10 text-gray-500" />;
        }
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'success':
                return {
                    bg: 'bg-green-50',
                    text: 'text-green-700',
                    border: 'border-green-200',
                    gradient: 'from-green-400 to-emerald-500',
                    light: 'bg-green-50',
                    dark: 'bg-green-600',
                    badge: 'bg-gradient-to-r from-green-500 to-emerald-600',
                    iconBg: 'bg-green-100'
                };
            case 'pending':
                return {
                    bg: 'bg-amber-50',
                    text: 'text-amber-700',
                    border: 'border-amber-200',
                    gradient: 'from-amber-400 to-orange-500',
                    light: 'bg-amber-50',
                    dark: 'bg-amber-600',
                    badge: 'bg-gradient-to-r from-amber-500 to-orange-600',
                    iconBg: 'bg-amber-100'
                };
            case 'failed':
                return {
                    bg: 'bg-red-50',
                    text: 'text-red-700',
                    border: 'border-red-200',
                    gradient: 'from-red-400 to-rose-500',
                    light: 'bg-red-50',
                    dark: 'bg-red-600',
                    badge: 'bg-gradient-to-r from-red-500 to-rose-600',
                    iconBg: 'bg-red-100'
                };
            default:
                return {
                    bg: 'bg-gray-50',
                    text: 'text-gray-700',
                    border: 'border-gray-200',
                    gradient: 'from-gray-400 to-gray-500',
                    light: 'bg-gray-50',
                    dark: 'bg-gray-600',
                    badge: 'bg-gradient-to-r from-gray-500 to-gray-600',
                    iconBg: 'bg-gray-100'
                };
        }
    };

    const getStatusText = (status) => {
        switch (status?.toLowerCase()) {
            case 'success':
                return 'Payment Successful';
            case 'pending':
                return 'Payment Pending';
            case 'failed':
                return 'Payment Failed';
            default:
                return status || 'Unknown Status';
        }
    };

    const handleDownloadReceipt = () => {
        if (!paymentStatus?.result) return;
        alert(`Receipt download for Registration ID: ${paymentStatus.result.Reg_Id}`);
        // In real implementation, this would trigger a PDF download
    };

    const closeModal = () => {
        setShowModal(false);
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50/30">
            {/* Header */}
            <div className="sticky top-0 z-10 bg-gradient-to-r from-slate-900 via-blue-900 to-slate-900 px-4 py-3 shadow-lg">
                <div className="max-w-7xl mx-auto flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-14 h-10 flex items-center justify-center bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
                            <img
                                src="/EMS/Images/Logo.png"
                                alt="PSG Logo"
                                className="w-12 h-12 object-contain"
                            />
                        </div>
                        <div className="text-white">
                            <div className="text-xs font-semibold leading-tight">
                                Centre for Non-Formal & Continuing Education (CNCE)
                            </div>
                            <div className="text-[10px] font-medium text-blue-200">
                                Event Payment Status Check
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <div className="hidden sm:block px-3 py-1 bg-gradient-to-r from-blue-600/30 to-cyan-600/30 rounded-lg">
                            <span className="text-sm font-bold text-white/90">
                                Secure Payment Portal
                            </span>
                        </div>
                        <div className="w-16 h-10 flex items-center justify-center bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
                            <img
                                src="/EMS/Images/Psg-100.png"
                                alt="100 Years Celebration"
                                className="w-12 h-12 object-contain"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-2xl mx-auto p-4 pt-8">
                {/* Welcome Card */}
                <div className="bg-gradient-to-r from-blue-600 to-blue-800 p-6 text-center shadow-xl">
                    <div className="flex items-center justify-center gap-3 mb-4">
                        <IndianRupee className="w-8 h-8 text-white" />
                        <h1 className="text-2xl font-bold text-white">
                            Payment Status
                        </h1>
                    </div>
                    <p className="text-blue-100 text-sm">
                        Enter your details to check payment status
                    </p>
                </div>

                {/* Search Card */}
                <div className="bg-white shadow-xl p-6 border border-slate-200">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-3 bg-gradient-to-br from-blue-100 to-cyan-100 rounded-xl">
                            <Search className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-slate-800">
                                Check Your Status
                            </h2>
                            <p className="text-slate-600 text-sm">
                                Verify your payment in seconds
                            </p>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit}>
                        <div className="space-y-4">
                            {/* Mobile Number */}
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">
                                    Mobile Number
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Phone className="h-4 w-4 text-slate-400" />
                                    </div>
                                    <input
                                        type="tel"
                                        value={mobileNumber}
                                        onChange={(e) => setMobileNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
                                        placeholder="10-digit mobile number"
                                        className="w-full pl-10 pr-3 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-sm"
                                        required
                                        disabled={loading}
                                        maxLength={10}
                                    />
                                </div>
                            </div>

                            {/* Event Dropdown */}
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">
                                    Select Event
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Calendar className="h-4 w-4 text-slate-400" />
                                    </div>
                                    <select
                                        value={selectedEvent}
                                        onChange={(e) => setSelectedEvent(e.target.value)}
                                        className="w-full pl-10 pr-3 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-sm appearance-none"
                                        required
                                        disabled={loading || fetchingEvents}
                                    >
                                        <option value="">Select an event</option>
                                        {events.map((event) => (
                                            <option key={event.Event_Id} value={event.Event_Id}>
                                                {event.Event_Name}
                                            </option>
                                        ))}
                                    </select>
                                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                        <svg className="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                                        </svg>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Error Message */}
                        {error && (
                            <div className="mt-4 p-3 bg-gradient-to-r from-red-50 to-red-100 border border-red-200 rounded-xl animate-fadeIn">
                                <div className="flex items-center gap-2 text-red-700">
                                    <XCircle className="w-4 h-4 flex-shrink-0" />
                                    <span className="text-sm font-medium">{error}</span>
                                </div>
                            </div>
                        )}

                        {/* Buttons */}
                        <div className="flex gap-3 mt-6">
                            <button
                                type="button"
                                onClick={handleClear}
                                disabled={loading}
                                className="flex-1 px-4 py-3 border-2 border-slate-300 text-slate-700 font-medium rounded-xl hover:bg-slate-50 transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 text-sm"
                            >
                                <X className="w-4 h-4" />
                                Clear
                            </button>
                            <button
                                type="submit"
                                disabled={loading || !mobileNumber || !selectedEvent}
                                className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-medium rounded-xl hover:from-blue-700 hover:to-cyan-700 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
                            >
                                {loading ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                        Checking...
                                    </>
                                ) : (
                                    <>
                                        <Search className="w-4 h-4" />
                                        Check Status
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>

                {/* Info Section */}
                <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-4 border border-blue-200">
                        <div className="flex items-center gap-3">
                            <Shield className="w-5 h-5 text-blue-600" />
                            <span className="text-sm font-semibold text-slate-800">Secure</span>
                        </div>
                        <p className="text-xs text-slate-600 mt-1">Your data is protected</p>
                    </div>
                    <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl p-4 border border-emerald-200">
                        <div className="flex items-center gap-3">
                            <Check className="w-5 h-5 text-emerald-600" />
                            <span className="text-sm font-semibold text-slate-800">Verified</span>
                        </div>
                        <p className="text-xs text-slate-600 mt-1">Real-time verification</p>
                    </div>
                    <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-200">
                        <div className="flex items-center gap-3">
                            <Award className="w-5 h-5 text-amber-600" />
                            <span className="text-sm font-semibold text-slate-800">Official</span>
                        </div>
                        <p className="text-xs text-slate-600 mt-1">PSG College Portal</p>
                    </div>
                </div>
            </div>

            {/* Payment Status Modal */}
            {showModal && paymentStatus?.result && (
                <>
                    {/* Backdrop */}
                    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 animate-fadeIn"></div>

                    {/* Modal */}
                    <div className="fixed inset-0 flex items-center justify-center p-4 z-50">
                        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full animate-slideUp">
                            {/* Modal Header */}
                            <div className={`p-6 rounded-t-2xl ${getStatusColor(paymentStatus.result.Status).light} border-b ${getStatusColor(paymentStatus.result.Status).border}`}>
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-3">
                                        <div className={`p-2 rounded-lg ${getStatusColor(paymentStatus.result.Status).iconBg}`}>
                                            {getStatusIcon(paymentStatus.result.Status)}
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-bold text-slate-800">
                                                Payment Status
                                            </h3>
                                            <p className="text-sm text-slate-600">
                                                {getSelectedEventName()}
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={closeModal}
                                        className="p-2 hover:bg-white/50 rounded-lg transition-colors"
                                    >
                                        <X className="w-5 h-5 text-slate-500" />
                                    </button>
                                </div>

                                {/* Status Badge */}
                                <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${getStatusColor(paymentStatus.result.Status).badge} text-white font-bold`}>
                                    {getStatusIcon(paymentStatus.result.Status)}
                                    {getStatusText(paymentStatus.result.Status)}
                                </div>
                            </div>

                            {/* Modal Body */}
                            <div className="p-6">
                                {/* Amount Card */}
                                <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl p-5 mb-6 border border-blue-200 text-center">
                                    <div className="text-sm text-slate-600 mb-2">Amount Paid</div>
                                    <div className="text-4xl font-bold text-blue-600">
                                        ₹{paymentStatus.result.Txn_Amount}
                                    </div>
                                    <div className="text-xs text-slate-500 mt-2">
                                        {paymentStatus.result.Status.toLowerCase() === 'success' ? 'Successfully paid' : 'Payment status'}
                                    </div>
                                </div>

                                {/* Details Grid */}
                                <div className="grid grid-cols-2 gap-4 mb-6">
                                    <div className="bg-slate-50 rounded-xl p-4">
                                        <div className="flex items-center gap-2 mb-2">
                                            <Phone className="w-4 h-4 text-blue-500" />
                                            <span className="text-xs font-semibold text-slate-600">Mobile</span>
                                        </div>
                                        <div className="text-sm font-semibold text-slate-800">
                                            {mobileNumber}
                                        </div>
                                    </div>
                                    <div className="bg-slate-50 rounded-xl p-4">
                                        <div className="flex items-center gap-2 mb-2">
                                            <Calendar className="w-4 h-4 text-blue-500" />
                                            <span className="text-xs font-semibold text-slate-600">Event</span>
                                        </div>
                                        <div className="text-sm font-semibold text-slate-800 truncate">
                                            {getSelectedEventName()}
                                        </div>
                                    </div>
                                </div>

                                {/* Additional Info */}
                                <div className="bg-emerald-50 rounded-xl p-4 mb-6 border border-emerald-200">
                                    <div className="flex items-start gap-3">
                                        <Shield className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                                        <div>
                                            <p className="text-sm text-emerald-800">
                                                <span className="font-semibold">Note:</span> This is your official payment confirmation.
                                                Keep this for your records.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Action Buttons */}
                                <div className="flex gap-3">
                                    <button
                                        onClick={closeModal}
                                        className="flex-1 px-4 py-3 border-2 border-slate-300 text-slate-700 font-medium rounded-xl hover:bg-slate-50 transition-all duration-200 flex items-center justify-center gap-2"
                                    >
                                        Close
                                    </button>
                                    <button
                                        onClick={handleDownloadReceipt}
                                        className={`flex-1 px-4 py-3 text-white font-medium rounded-xl transition-all duration-200 flex items-center justify-center gap-2 ${getStatusColor(paymentStatus.result.Status).badge} shadow-md hover:shadow-lg`}
                                    >
                                        <Download className="w-4 h-4" />
                                        Receipt
                                    </button>
                                </div>
                            </div>

                            {/* Modal Footer */}
                            <div className="px-6 py-4 bg-slate-50 rounded-b-2xl border-t border-slate-200">
                                <div className="flex items-center justify-center gap-2">
                                    <Shield className="w-4 h-4 text-slate-400" />
                                    <span className="text-xs text-slate-500">
                                        PSG College of Technology • Secure Payment Portal
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* CSS Animations */}
            <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        .animate-slideUp {
          animation: slideUp 0.4s ease-out;
        }
      `}</style>
        </div>
    );
};

export default PaymentStatus;